import { useMemo } from 'react'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { SellDownloadPdfTemplateButton, SellDownloadButtonPDF, SellPdfPreview, SellViewWorkSheetButton, SellCreateWorkSheetButton, SellDownloadButtonCSV, SellQuickAccessButton, SellDownloadTags } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { SellDeliveryNoteDuplicateButton } from '../../../SellDeliveryNButtons'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import { getAllWorkSheetGroupStatuses } from '../../../../../../../../../../../utils/reducers/getters'
import { OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'

const SellDeliveryNoteFinishedStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useSellDeliveryNoteFinishedStepButtonsLogic, ...rest }} className='mr-8' />
    )
}

const useSellDeliveryNoteFinishedStepButtonsLogic = ({ data, onSubmit, isReadOnly }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const name = 'sell_delivery_note_finished_step_buttons'
    const Order = data?.header
    const workSheets = getAllWorkSheetGroupStatuses()
    const hideSellQuickAccessButton = !Order?.QuoteOrderId && !Order?.SellOrderId && !Order?.SellDeliveryNoteId && !Order?.SellInvoiceId && !Order?.WorkSheetId && !Order?.relatedSellOrders?.length && !Order?.relatedSellDeliveryNotes?.length && !Order?.relatedSellInvoices?.length && !Order?.relatedWorkSheets?.length && !Order?.relatedOrderRefunds?.length

    const defaultButtons = [{ "id": "previewSellDeliveryNote" }, { "id": "downloadSellDeliveryNote" }, { "id": "quickAccess" }, { "id": "createWorkSheet" },{ "id": "viewWorkSheet" }]
    
    const sellDeliveryNoteDuplicateText = t('duplicateDeliveryNote', 'Duplicar albarán')
    const sellDeliveryNoteDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const sellDeliveryNoteDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const sellDeliveryNoteViewWorkSheetText = t('goToWorkSheet', 'Ir a la hoja de trabajo')
    const sellDeliveryNoteCreateWorkSheetText = t('createWorkSheet', 'Crear hoja de trabajo')
    const sellDeliveryNoteQuickAccessText = t('quickAccess', 'Acceso rápido')
    const sellDeliveryNotePreviewPDFText = t('previewPdf', 'Previsualizar PDF')
    const sellDeliveryNoteDownloadPdfTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const sellDeliveryNoteDownloadPdfText = t('downloadPdf', 'Descargar PDF')
    const sellDeliveryNoteDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const sellDeliveryNoteVariablesText = t('variables', 'Variables')
    const sellDeliveryNoteProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateSell: {
                component: SellDeliveryNoteDuplicateButton,
                extraProps: {
                    title: sellDeliveryNoteDuplicateText,
                    id: 'duplicateSell',
                    allRowData: data,
                }
            },
            downloadSellCsv: {
                component: SellDownloadButtonCSV,
                extraProps: {
                    title: sellDeliveryNoteDownloadCSVText,
                    id: 'downloadSellCsv',
                    Order,
                    typeTemplate:'sellDeliveryNote',
                }
            },
            downloadSellTag: {
                component: SellDownloadTags,
                extraProps: {
                    title: sellDeliveryNoteDownloadTagsText,
                    id: 'downloadSellTag',
                    Order,
                    typeTemplate:'sellDeliveryNote',
                }
            },
            viewWorkSheet: {
                component: SellViewWorkSheetButton,
                showButton: Order?.WorkSheetId,
                extraProps: {
                    title: sellDeliveryNoteViewWorkSheetText,
                    id: 'viewWorkSheet',
                    WorkSheetId: Order?.WorkSheetId
                }
            },
            createWorkSheet: {
                component: SellCreateWorkSheetButton,
                showButton: !Order?.WorkSheetId && !!workSheets?.WorkSheetActiveStatusGroups?.length,
                extraProps: {
                    title: sellDeliveryNoteCreateWorkSheetText,
                    id: 'createWorkSheet',
                    Order,
                    createURL: '/api/app/order/sell/workSheet/createWorkSheetBySellDeliveryNote',
                    orderField: 'SellDeliveryNoteId',
                    completeData: data 
                }
            },
            quickAccess: {
                component: SellQuickAccessButton,
                showButton: !hideSellQuickAccessButton,
                extraProps: {
                    title: sellDeliveryNoteQuickAccessText,
                    id: 'quickAccess',
                    Order
                }
            },
            previewSellDeliveryNote: {
                component: SellPdfPreview,
                extraProps: {
                    title: sellDeliveryNotePreviewPDFText,
                    id: 'previewSellDeliveryNote',
                    Order,
                    typeTemplate:'sellDeliveryNote',
                }
            },
            downLoadSellDeliveryNoteTemplate: {
                component: SellDownloadPdfTemplateButton,
                extraProps: {
                    title: sellDeliveryNoteDownloadPdfTemplateText,
                    id: 'downLoadSellDeliveryNoteTemplate',
                    Order,
                    key:'downloadSellDeliveryNotePDFTemplate',
                    typeTemplate:'sellDeliveryNote',
                }
            },
            downloadSellDeliveryNote: {
                component: SellDownloadButtonPDF,
                extraProps: {
                    title: sellDeliveryNoteDownloadPdfText,
                    id: 'downloadSellDeliveryNote',
                    Order,
                    key:'downloadSellDeliveryNote',
                    typeTemplate:'sellDeliveryNote',
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: sellDeliveryNoteDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    onSubmit,
                    isReadOnly,
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: sellDeliveryNoteVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    isReadOnly,
                    onSubmit,
                }
            },
            sellProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: sellDeliveryNoteProductTypesText,
                    id: 'sellProductTypes',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
        }
    }, [Order, data, hideSellQuickAccessButton, onSubmit, sellDeliveryNoteDownloadCSVText, sellDeliveryNoteDownloadPdfTemplateText, sellDeliveryNoteDownloadPdfText, sellDeliveryNoteDownloadTagsText, sellDeliveryNoteDuplicateText, sellDeliveryNotePreviewPDFText, sellDeliveryNoteQuickAccessText, sellDeliveryNoteViewWorkSheetText, sellDeliveryNoteDivideIntoChaptersText, sellDeliveryNoteVariablesText, isReadOnly, sellDeliveryNoteCreateWorkSheetText, workSheets?.WorkSheetActiveStatusGroups?.length, sellDeliveryNoteProductTypesText])

    return { ButtonComponents, name, defaultButtons }
}

export const SellDeliveryNoteFinishedStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useSellDeliveryNoteFinishedStepButtonsLogic, ...rest }} className='mr-8' />
    )
}

export default SellDeliveryNoteFinishedStepButtons